// Single source of truth for the customer-facing helipad fee under the
// "Option Y" model — helipad fees are additive line items on top of the
// route price. Defined here so Commit 1a can plumb the schema + portal UI
// without touching the customer payment path. Commit 3 wires this into
// App.jsx + bookings-create.js + emails.
//
// Resolution chain (highest priority first):
//   1. operator.helipadOverrides[] entry where helipadRef._ref === location._id
//      → customer fee = ceil(brokerFee / (1 - commission) / 10) × 10
//      (0 broker fee → 0 customer fee, no rounding)
//   2. location.fee (global default — informational TODAY, additive in Option Y)
//   3. 0
//
// Emergency kill-switch: set globalThis.FEATURE_HELIPAD_OVERRIDES = false
// to ignore overrides and use only the global location.fee.
//
// P2 #9 phase 4 — the customer fee is broker + commission markup
// (brokerToCustomer), no longer broker / (1 − rate).

import { brokerToCustomer } from '../pricing.js';

/**
 * @param {Object} location — Sanity location doc (or its projection). Needs _id + fee.
 * @param {Object|null} operator — Sanity operator doc (or projection) with helipadOverrides[].
 * @param {number} [commissionPct=10] — operator/default commission %.
 * @returns {{ customerFee:number, brokerFee:number|null, source:'override'|'global'|'none' }}
 */
export function getHelipadFee(location, operator, rate = 10, allowLocationFee = false) {
  // Emergency rollback. When false, the helper returns 0 everywhere → no
  // helipad fees are added to the customer total.
  if (typeof globalThis !== 'undefined' && globalThis.FEATURE_HELIPAD_OVERRIDES === false) {
    return { customerFee: 0, brokerFee: null, source: 'disabled' };
  }
  // Brief 2 — landing-fee SOURCE depends on the pricing mode, passed by the
  // caller as allowLocationFee (= the price came from a route price: manual /
  // auto / override). For those modes the pad fee is additive on top of the
  // flight price: operator.helipadOverrides[].brokerFee (operator-specific) if
  // set, ELSE location.fee (global default — reactivated here). For a pure
  // cost-model compute (allowLocationFee = false) the pad fee comes from the
  // cost-model chain (aircraftPricing.airport/additional), so this returns 0 —
  // NEVER add location.fee there (would double-count). All fees are operator-
  // NET; brokerToCustomer applies the markup + VAT 13% once.
  if (!allowLocationFee) {
    return { customerFee: 0, brokerFee: null, source: 'costmodel' };
  }
  if (location && operator && Array.isArray(operator.helipadOverrides)) {
    const override = operator.helipadOverrides.find(o => {
      const ref = o?.helipadRef?._ref || o?.helipadRef;
      return ref && ref === location._id;
    });
    if (override && override.brokerFee != null) {
      const broker = Number(override.brokerFee);
      if (Number.isFinite(broker) && broker > 0) {
        // operator-specific override REPLACES location.fee (not additive).
        return { customerFee: brokerToCustomer(broker, rate), brokerFee: broker, source: 'override' };
      }
    }
  }
  // Global default: location.fee (operator-NET). Reactivated for manual/auto/override.
  const locFee = location ? Number(location.fee) : 0;
  if (Number.isFinite(locFee) && locFee > 0) {
    return { customerFee: brokerToCustomer(locFee, rate), brokerFee: locFee, source: 'global' };
  }
  return { customerFee: 0, brokerFee: null, source: 'none' };
}

/** Shorthand for callers that only need the customer-facing number. */
export function getCustomerHelipadFee(location, operator, commissionPct = 10, allowLocationFee = false) {
  return getHelipadFee(location, operator, commissionPct, allowLocationFee).customerFee;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-pad, per-direction pad-fee model (2026-06-05) — ONE source for ALL pricing
// modes. Replaces both the single getHelipadFee(location.fee) and the cost-model
// flat airport{4}. All values NET (excl. VAT 13%); markup + VAT are added once on
// the total downstream. NOT wired into the live price path until Money-#1 is
// approved — pure + unit-verifiable here.
// ─────────────────────────────────────────────────────────────────────────────

// Per-aircraft-class fee row from a `classes[]` array (location.padFees.classes
// or helipadOverrides[].classes), or null. Matched on the exact feeClass key.
function classRow(classes, feeClass) {
  if (!Array.isArray(classes) || !feeClass) return null;
  return classes.find(c => c && c.feeClass === feeClass) || null;
}

// Effective per-field pad fee for ONE pad, for a given aircraft fee class.
// Precedence per field (most specific first):
//   operator override — class row (non-null, incl. 0) → flat override (non-null)
//   → location default — class row (>0) → flat default (>0) → 0.
// feeClass = null (or no matching class row) → the FLAT fields only, i.e. exactly
// the pre-class behaviour (operator override → location default → 0). Returns
// {departure,landing,handling,special}.
export function resolvePadFees(location, operator, feeClass = null) {
  const defFlat = (location && location.padFees) || {};
  const defClass = feeClass ? classRow(defFlat.classes, feeClass) : null;
  let ovFlat = null, ovClass = null;
  if (location && location._id && operator && Array.isArray(operator.helipadOverrides)) {
    ovFlat = operator.helipadOverrides.find(o => (o?.helipadRef?._ref || o?.helipadRef) === location._id) || null;
    ovClass = (ovFlat && feeClass) ? classRow(ovFlat.classes, feeClass) : null;
  }
  const pick = (f) => {
    // Per-aircraft-class rows SUPPLEMENT the flat fields PER FIELD: a class field is
    // applied ONLY when it is a positive number — an unset OR 0 class field falls
    // through to the flat override → location default and NEVER zeroes a flat fee
    // (fixes AS365 losing the flat Koropi departure 200 when its class row set only
    // handling). Explicit 0 ("charge nothing") is honored ONLY at the operator FLAT
    // override level, not per-class.
    if (ovClass && ovClass[f] != null && Number.isFinite(Number(ovClass[f])) && Number(ovClass[f]) > 0) return Number(ovClass[f]);
    if (ovFlat && ovFlat[f] != null && Number.isFinite(Number(ovFlat[f])) && Number(ovFlat[f]) >= 0) return Number(ovFlat[f]); // flat override wins incl. explicit 0
    if (defClass && defClass[f] != null && Number(defClass[f]) > 0) return Number(defClass[f]);
    if (defFlat && defFlat[f] != null && Number(defFlat[f]) > 0) return Number(defFlat[f]);
    return 0;
  };
  return {
    departure: pick('departure'), landing: pick('landing'), handling: pick('handling'), special: pick('special'),
    // B) HELISTAR per-pax airport fees (NET) — departurePaxFee at the revenue
    // boarding pad, arrivalPaxFee at the revenue deplaning pad, EACH × pax. Same
    // precedence as the 4 flat fees (operator-class → operator-flat → location-class
    // → location-flat → 0). Multiplication × pax + pax-leg-only scoping happens in
    // routePaxFeesNet (NOT here — touchPadFeeNet is pax-agnostic flat).
    departurePaxFee: pick('departurePaxFee'), arrivalPaxFee: pick('arrivalPaxFee'),
  };
}

// NET fee for ONE touch of a pad. handling + special apply PER TOUCH.
//   'departure'    (revenue boarding pad) → departure + handling + special
//   'arrival'      (revenue deplaning pad) → landing  + handling + special
//   'base-outbound'(base ≠ pickup, positioning) → departure + handling ONCE (no landing/special)
//   'ferry-arrival'(empty positioning/return landing, ONLY when the operator opts in
//                   via chargeFerryLandings) → landing + handling (NO special: empty leg,
//                   no passengers — mirrors base-outbound excluding the pax-only special)
export function touchPadFeeNet(eff, role) {
  if (role === 'departure') return eff.departure + eff.handling + eff.special;
  if (role === 'arrival') return eff.landing + eff.handling + eff.special;
  if (role === 'base-outbound') return eff.departure + eff.handling;
  if (role === 'ferry-arrival') return eff.landing + eff.handling; // empty leg: landing+handling, no special
  return 0; // any other empty touch → 0
}

// One one-way base-round's pad touches — the single reusable unit. A one-way charter
// flies THREE legs: base→pickup (empty positioning), pickup→dropoff (revenue), and
// dropoff→base (empty return-to-base ferry). The flat DEPARTURE fee is charged once at
// the ORIGIN pad of EVERY flown leg, mirroring HELISTAR:
//   base-outbound (base ≠ pickup)   → leg 1 origin = base    → base DEPARTURE (+handling)
//   departure (pickup)              → leg 2 origin = pickup  → pickup DEPARTURE (+handling+special)
//   arrival (dropoff)               → leg 2 dest   = dropoff → dropoff LANDING  (+handling+special)
//   base-outbound (base ≠ dropoff)  → leg 3 origin = dropoff → dropoff DEPARTURE (+handling)  ← was MISSING
// The return-ferry departure reuses the 'base-outbound' role (departure + handling, NO
// special — empty leg, no passengers), exactly as sightseeingPadTouches reuses it for
// the empty pad→base ferry departure (there is no dedicated 'ferry-departure' role).
// Guarded on base ≠ dropoff so a drop-off AT the base (no return leg) charges nothing,
// and handling is deduped per pad per segment so the dropoff's existing arrival-touch
// handling is never doubled. When dropoff has DEPARTURE = 0 (e.g. a heliport with no
// departure fee) this adds nothing — so the previously-correct routes do not change.
//   + the two EMPTY ferry landings when the operator opts in via
//     operator.chargeFerryLandings: positioning inbound (empty landing at the
//     pickup, base ≠ pickup) + return-to-base ferry (empty landing at base, after
//     the dropoff, base ≠ dropoff). 'ferry-arrival' = landing + handling, no special.
function oneWayTouches({ baseLocation, depLocation, arrLocation, operator }) {
  const t = [];
  if (baseLocation && baseLocation._id && depLocation && depLocation._id && baseLocation._id !== depLocation._id) {
    t.push({ location: baseLocation, operator, role: 'base-outbound' });
  }
  if (depLocation) t.push({ location: depLocation, operator, role: 'departure' });
  if (arrLocation) t.push({ location: arrLocation, operator, role: 'arrival' });
  // Return-to-base ferry departure: the empty dropoff → base leg DEPARTS the dropoff
  // pad, so its flat DEPARTURE fee is charged once (base ≠ dropoff). Reuses
  // 'base-outbound' semantics (departure + handling, no special / no pax — empty leg).
  if (baseLocation && baseLocation._id && arrLocation && arrLocation._id && baseLocation._id !== arrLocation._id) {
    t.push({ location: arrLocation, operator, role: 'base-outbound' });
  }
  if (operator && operator.chargeFerryLandings && baseLocation && baseLocation._id) {
    if (depLocation && depLocation._id && baseLocation._id !== depLocation._id) {
      t.push({ location: depLocation, operator, role: 'ferry-arrival' });
    }
    if (arrLocation && arrLocation._id && baseLocation._id !== arrLocation._id) {
      t.push({ location: baseLocation, operator, role: 'ferry-arrival' });
    }
  }
  return t;
}

// Build the canonical touch list for a route — the SINGLE source every caller
// (server computeExpectedTotal + ALL client price sites + the simulator) uses, so
// they never diverge in touch construction.
//   one-way    = one base-round's touches (oneWayTouches).
//   round trip = the one-way base-round flown TWICE — the heli ferries HOME
//                between dropping the customer off and picking them up again, it
//                does NOT wait. So the pad fees are EXACTLY 2× one-way by
//                construction (every pad touched twice, chargeFerryLandings applied
//                per segment), matching the route-engine.js buildSequence geometry.
// (Was a single base round + a return revenue leg — which collapsed to ~1× one-way
// when base==pickup, undercharging the round trip's landing fees by ~half.)
// Each touch carries a `seg` index (0 = the one-way round; 1 = the second round
// of a round trip) so the per-pad handling/special are charged ONCE PER SEGMENT,
// not deduped across the whole round trip — RT stays exactly 2× one-way.
export function buildPadTouches({ baseLocation, depLocation, arrLocation, operator, isRoundTrip = false }) {
  const ow = oneWayTouches({ baseLocation, depLocation, arrLocation, operator });
  const seg0 = ow.map(t => ({ ...t, seg: 0 }));
  return isRoundTrip ? [...seg0, ...ow.map(t => ({ ...t, seg: 1 }))] : seg0;
}

// Sightseeing-loop pad touches (HELISTAR geometry, verified 1:1 SX-HDY + SX-HGH).
// The heli ferries from its base to the chosen pad, flies the loop, ferries home —
// 3 legs: base→pad (empty positioning), pad→pad (the loop), pad→base (empty return).
// Fees by touch (departure/landing summed PER touch; handling ONCE per pad per ground
// STAY/seg; pax at the REVENUE loop departure only):
//   base: 1× departure (positioning out) + 1× landing (ferry home).
//   pad : 2× departure (loop start + ferry-back start) + 2× landing (ferry-in + loop end)
//         + 2× handling (TWO ground stays: the pickup stay + the loop stay).
//   pax : departurePax × pax at the revenue loop departure (1×); arrivalPax at the loop
//         arrival. Empty positioning/ferry touches carry no pax.
// 'base-outbound' is reused for the empty pad→base ferry departure: it adds the pad
// departure fee + handling (already counted for that seg) but NO special and NO pax —
// exactly the empty-leg semantics we need (there is no dedicated 'ferry-departure' role).
// base == pad (heli based AT the sightseeing pad): no positioning — a SINGLE loop stay
// at the pad (1 departure + 1 landing + 1 handling + pax).
export function sightseeingPadTouches({ baseLocation, padLocation, operator }) {
  const pad = padLocation;
  if (!pad || !pad._id) return [];
  const base = baseLocation;
  const samePad = base && base._id && base._id === pad._id;
  if (!base || !base._id || samePad) {
    return [
      { location: pad, operator, role: 'departure', seg: 0 },
      { location: pad, operator, role: 'arrival', seg: 0 },
    ];
  }
  return [
    { location: base, operator, role: 'base-outbound', seg: 0 }, // positioning departure from base
    { location: pad, operator, role: 'ferry-arrival', seg: 0 },  // stay A: empty landing to pick up (+ handling seg0)
    { location: pad, operator, role: 'departure', seg: 0 },      // stay A: revenue departure for the loop (+ departurePax)
    { location: pad, operator, role: 'arrival', seg: 1 },        // stay B: revenue arrival ending the loop (+ handling seg1)
    { location: pad, operator, role: 'base-outbound', seg: 1 },  // stay B: empty ferry departure back to base (dep fee, no pax)
    { location: base, operator, role: 'ferry-arrival', seg: 2 }, // empty landing at base
  ];
}

// Structured NET breakdown of all touches (HELISTAR model). touches =
// [{ location, operator, role, seg }]. departure + landing are summed PER TOUCH
// (every actual take-off / landing), but handling + special are charged ONCE per
// distinct pad PER SEGMENT — NOT per touch — so a pad touched twice in one
// direction (e.g. base==departure plus its ferry landing back) is not
// double-charged handling. `byPad` is the per-pad component split for display.
// `feeClass` selects the per-aircraft-class fee row (null → flat).
export function routePadFeeBreakdown(touches, feeClass = null) {
  let departure = 0, landing = 0, handling = 0, special = 0;
  const handlingSeen = new Set(); // `${padId}|${seg}`
  const specialSeen = new Set();  // `${padId}|${seg}` (revenue touch only)
  const byPad = {};
  for (const t of (touches || [])) {
    if (!t || !t.location) continue;
    const eff = resolvePadFees(t.location, t.operator, feeClass);
    const padId = t.location._id || t.location.id || '';
    const seg = t.seg || 0;
    const key = padId + '|' + seg;
    const bp = byPad[padId] || (byPad[padId] = { name: t.location.name || padId, departure: 0, landing: 0, handling: 0, special: 0 });
    if (t.role === 'departure' || t.role === 'base-outbound') { departure += eff.departure; bp.departure += eff.departure; }
    if (t.role === 'arrival' || t.role === 'ferry-arrival') { landing += eff.landing; bp.landing += eff.landing; }
    if (!handlingSeen.has(key)) { handlingSeen.add(key); handling += eff.handling; bp.handling += eff.handling; }
    if ((t.role === 'departure' || t.role === 'arrival') && !specialSeen.has(key)) { specialSeen.add(key); special += eff.special; bp.special += eff.special; }
  }
  return { departure, landing, handling, special, total: departure + landing + handling + special, byPad };
}

// NET Σ of all touches on a route — the single money number (HELISTAR model:
// handling/special once per pad per segment). `feeClass` selects the aircraft
// class fee row (null → flat fields, pre-class behaviour).
export function routePadFeesNet(touches, feeClass = null) {
  return routePadFeeBreakdown(touches, feeClass).total;
}

// B) NET Σ of the HELISTAR per-pax airport fees over a route's touches —
// departurePaxFee at the revenue DEPARTURE touch + arrivalPaxFee at the revenue
// ARRIVAL touch, EACH × pax. ONLY the passenger-carrying legs count: empty
// positioning ('base-outbound') / ferry ('ferry-arrival') touches carry 0 pax →
// excluded. Once per pad per direction by construction (each one-way segment has
// exactly one 'departure' + one 'arrival' touch; RT = 2 segments = 2× one-way).
// Booked as "Additional Costs" (separate from Airport & Handling), summed into the
// operator net before the final VAT + 100-rounding. `byPad` for display.
export function routePaxFeeBreakdown(touches, feeClass = null, pax = 1) {
  const p = Number(pax) > 0 ? Number(pax) : 1;
  let total = 0;
  const byPad = {};
  for (const t of (touches || [])) {
    if (!t || !t.location) continue;
    const fee = (t.role === 'departure') ? 'departurePaxFee' : (t.role === 'arrival') ? 'arrivalPaxFee' : null;
    if (!fee) continue; // pax legs only — positioning/ferry excluded
    const eff = resolvePadFees(t.location, t.operator, feeClass);
    const amt = (Number(eff[fee]) || 0) * p;
    if (amt <= 0) continue;
    const padId = t.location._id || t.location.id || '';
    const bp = byPad[padId] || (byPad[padId] = { name: t.location.name || padId, departurePax: 0, arrivalPax: 0 });
    if (t.role === 'departure') bp.departurePax += amt; else bp.arrivalPax += amt;
    total += amt;
  }
  return { total, byPad, pax: p };
}

export function routePaxFeesNet(touches, feeClass = null, pax = 1) {
  return routePaxFeeBreakdown(touches, feeClass, pax).total;
}
